import {
  Accessory,
  AvailabilityState,
  PersonalPrice,
  Product as GlobalProduct,
  SubProducts
} from '@features/product/typings';
import type { ReactNode } from 'react';

// Const

export enum CollectionType {
  PROPERTY = 'PROPERTY',
  DROPDOWN = 'DROPDOWN'
}

export enum PresentationFormat {
  CARDS_WITH_BUNDLE = 'CARDS_WITH_BUNDLE',
  CARDS_WITH_DISCOUNTS = 'CARDS_WITH_DISCOUNTS',
  CARDS_WITH_IMAGES = 'CARDS_WITH_IMAGES',
  CARDS_DEFAULT = 'CARDS_DEFAULT'
}

export enum Variant {
  DESKTOP = 'DESKTOP',
  MOBILE = 'MOBILE'
}

// Base props

export type BaseCollection = {
  id: string;
  type: CollectionType;
  title: string;
  isActive: boolean;
  attributes: Attributes;
  overarchingCollection: OverarchingCollectionType | null;
};

type Attributes = {
  isShowingPriceDifference: boolean;
  isColorSeparated: boolean;
};

// Collections

export type ResponseCollection = {
  discriminants: (Omit<Discriminant, 'values'> & {
    values: Omit<Value, 'product'>[];
  })[];
} & BaseCollection;

export type Collection = {
  discriminants: Discriminant[];
} & BaseCollection;

export type CollectionProps = {
  collection: Collection;
  mainImageId?: string;
  onProductClick?: (product: Product | null) => void;
  product: GlobalProduct;
  variant?: Variant;
  selectedProductId?: number;
  isInHideIPhoneAccessoriesVariation: boolean;
  usbCChargerComponent?: ReactNode;
  chargerData?: ChargerData;
};

export type ChargerData = {
  charger: Accessory;
  isInMissingChargerWordingSplitTest: boolean | undefined;
  showBoundingBox: boolean;
  showTitle: boolean;
};

export type Discriminant = {
  id: string;
  referenceId: string | null;
  displayName: string;
  presentationFormat: PresentationFormat;
  values: Value[];
};

export type Value = {
  id: string;
  name: string;
  description: string;
  isRecommended: boolean;
  group: {
    id: string;
  } | null;
  products: (Product | null)[];
  product: Product | null;
  isValueOfCurrentProduct: boolean;
  shouldShowValue: boolean;
};

export type OverarchingCollectionType = {
  entranceDescription: string;
  entranceTitle: string;
  title: string;
  products: (Product | null)[];
};

// Other

export type ProductType = {
  productTypeId: string | null;
  singularName: string;
};

export type Product = {
  id: number;
  nullableName?: string | null;
  urls: {
    product: string;
  };
  mediaGallery: {
    items: MediaGalleryItems[];
  };
  subProducts?: SubProducts[];
  nullableAvailabilityState: AvailabilityState | null;
  nullableSalesPrice: Price | null;
  listPrice?: Price | null;
  shouldRedirect?: boolean;
  brand?: { name?: string };
  prices?: {
    msrp?: Price;
  };
  personalPrice?: PersonalPrice;
};

export type Price = {
  includingVat: number;
};

export type MediaGalleryItems = {
  image: {
    id: string;
  };
  label: string;
};

export type InitialProduct = CollectionInitialProduct | SecondChanceCollectionInitialProduct;

export type CollectionInitialProduct = {
  availabilityState: AvailabilityState;
  price: number;
  listPrice?: number;
  redirectUrl: string;
  id: number;
  personalPrice?: PersonalPrice;
  brandName?: string;
};

export type SecondChanceCollectionInitialProduct = {
  id: string;
  price: number;
};
